import { useState, useEffect } from "react";
import {
  TagreadEntityList,
  TagreadEntity,
  TagreaderModel,
  TagreadModel,
} from "./api/Dtos";
import { GET, POST } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import {
  HStack,
  Input,
  Text,
  VStack,
  Button,
  Checkbox,
} from "@chakra-ui/react";

import { FastTable, isNumber } from "./Table/FastTable";
import {
  dateTimeInMsToString,
  timeInMsToStringUTC,
} from "./Services/ResultService";

const modifyFields = [""] as const;
const modifyDisplayLabels = [""];

const fixedFields = [
  "id",
  "firstSeenTimestamp",
  "heartBeat",
  "startNr",
  "antennaPort",
  "startGroup",
  "timeStart",
  "timeChip",
  "epc",
  "triggerOutput",
];

const fixedDisplayLabels = [
  "Klicka spara för att köra om DB-trigger. Id",
  "Tid RFID",
  "HeartBeat",
  "StartNr",
  "Antenn",
  "Startgrupp",
  "Starttid",
  "Löptid",
  "Tag",
  "TriggerOutput",
];

export function TagreadsList() {
  const globalContext = useGlobalContext();
  const [tagreads, setTagreads] = useState<Array<TagreadEntity>>([]);
  //const [filterClass, setFilterClass] = useState<string | undefined>(undefined);
  const [filterHeartbeat, setFilterHeartbeat] = useState<boolean | undefined>(
    undefined
  );
  const [filterStartnrOrName, setFilterStartnrOrName] = useState<
    string | undefined
  >(undefined);
  const [filteredTagreads, setFilteredTagreads] = useState<
    Array<TagreadEntity>
  >([]);

  useEffect(() => {
    fetchTagreads();
  }, []);

  // Get tagreads from backend
  const fetchTagreads = async () => {
    const response = await GET.allTagreads();
    const tagreadEntityList: TagreadEntityList = { tagreadEntitys: response };

    setFilteredTagreads(
      tagreadEntityList.tagreadEntitys.filter((r) => r.heartBeat === false)
    );
    setTagreads(tagreadEntityList.tagreadEntitys);
  };

  // Apply filtering
  useEffect(() => {
    let newTagreadsArray = [...tagreads];
    if (filterHeartbeat) {
      newTagreadsArray = newTagreadsArray.filter(
        (r) => r.heartBeat === filterHeartbeat
      );
    } else {
      newTagreadsArray = newTagreadsArray.filter((r) => r.heartBeat === false);
    }

    if (filterStartnrOrName) {
      if (isNumber(filterStartnrOrName)) {
        newTagreadsArray = newTagreadsArray.filter((r) => {
          return `${r.startNr}`.startsWith(filterStartnrOrName);
        });
      } else {
        newTagreadsArray = newTagreadsArray.filter((r) =>
          `${r.triggerOutput}`.includes(filterStartnrOrName)
        );
      }
    }

    setFilteredTagreads(newTagreadsArray);
  }, [tagreads, filterHeartbeat, filterStartnrOrName]);

  return (
    <div>
      <VStack align="start">
        <Text textAlign="left" fontSize="24" width="100%">
          Lästa taggar ({filteredTagreads.length.toString()} av{" "}
          {tagreads.length.toString()})
        </Text>
      </VStack>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
        marginTop={"-40px"}
      >
        <VStack>
          <HStack>
            <Button onClick={() => fetchTagreads()} colorScheme="blue">
              Uppdatera lista
            </Button>
          </HStack>
        </VStack>

        <VStack>
          <Text>Välj om heartbeat ska visas</Text>
          <Checkbox
            onChange={(event) => {
              if (event.currentTarget.checked) {
                setFilterHeartbeat(true);
              } else {
                setFilterHeartbeat(false);
              }
            }}
          ></Checkbox>
        </VStack>
        <VStack>
          <Text>Sök på triggerOutput eller startnummer</Text>
          <Input
            minW={100}
            placeholder="Sök startnr eller triggerOutput"
            onChange={(event) => {
              if (filterStartnrOrName === event.currentTarget.value) {
                setFilterStartnrOrName(undefined);
              } else {
                setFilterStartnrOrName(event.currentTarget.value);
              }
            }}
          />
        </VStack>
      </HStack>

      <FastTable
        data={filteredTagreads}
        pageHeader={`Lästa taggar till ${globalContext.competition?.tag ?? "Ingen tävling vald"} ${new Date().toISOString().split('T')[0]}`}
        repeatHeaderRows={3}
        downloadFileName={`LästaTaggarTill${globalContext.competition?.tag ?? "Ingen tävling vald"}_${new Date().toISOString().split('T')[0]}`}
        // rowHeight={75}
        headers={[
          "id",
          "Registrerad",
          "Läsarens tid",
          "Tagg",
          "Startnr",
          "Startgrp",
          "Starttid",
          "Tid chip",
          "HBeat",
          "Trigger",
        ]}
        rowFormat={
          "0.5fr 1.2fr 1.2fr 1.7fr 0.5fr 0.7fr 1.2fr 0.7fr 0.7fr 2fr 1fr"
        }

        toRowData={(p) => {
          return [
            String(p.id),
            dateTimeInMsToString(p.creationTime),
            dateTimeInMsToString(Math.round(p.firstSeenTimestamp / 1000)),
            p.epc,
            String(p.startNr),
            p.startGroup,
            dateTimeInMsToString(p.timeStart),
            timeInMsToStringUTC(p.timeChip),
            String(p.heartBeat),
            p.triggerOutput,
          ];
        }}
        modifiableFields={modifyFields as unknown as Array<keyof TagreadEntity>}
        modifiableFieldsDisplayName={modifyDisplayLabels}
        fixedFields={fixedFields as Array<keyof TagreadEntity>}
        fixedFieldsDisplayName={fixedDisplayLabels}
        onChange={async (repeatTagread) => {
          const newTagread: TagreadModel = {
            antennaPort: repeatTagread.antennaPort,
            epc: repeatTagread.epc,
            firstSeenTimestamp: repeatTagread.firstSeenTimestamp,
            isHeartBeat: repeatTagread.heartBeat,
          };

          const newTagreader: TagreaderModel = {
            reader_name: "ManualAdd",
            mac_address: "00:00:00:00:00:00",
            tag_reads: [newTagread],
          };

          if (globalContext?.competition?.tag !== undefined) {
            const success = await POST.tagread(newTagreader);
            if (success) {
              console.log("Added tagread:", newTagreader);
              //await fetchTagreads(); // Refresh the list
            }
          } else {
            console.error("Ingen tävling är vald.");
          }
          return true;
        }}
      />
    </div>
  );
}

export default TagreadsList;
